import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { SignalRUpdateMessage } from '../models/NotificationUpdate';
import { emitter, EVENT_CONSTANTS } from '../events/emitt';
import { SignalRPayload } from '../models/ActionModels';

const emitterObject = emitter;

export interface SignalRService {
    connection: HubConnection | null;
    isConnected: boolean;
    connect: (url: string, token: string) => Promise<void>;
    disconnect: () => Promise<void>;
    testConnection: (url: string, token: string) => Promise<boolean>;
}

class SignalRServiceImpl implements SignalRService {
    public connection: HubConnection | null = null;
    public isConnected: boolean = false;


    public async connect(url: string, token: string): Promise<void> {
        if (this.connection && this.isConnected) {
            console.log('SignalR: Already connected');
            return;
        }

        console.log('SignalR: Attempting to connect to:', url);
        console.log('SignalR: Using token:', token ? 'Token present' : 'No token');

        try {
            // Build the connection
            this.connection = new HubConnectionBuilder()
                .withUrl(url, {
                    accessTokenFactory: () => {
                        console.log('SignalR: Providing access token');
                        return token;
                    },
                    withCredentials: true,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    skipNegotiation: false
                })
                .withAutomaticReconnect([0, 2000, 10000, 30000]) // Retry delays
                .configureLogging(LogLevel.Information) // More detailed logging
                .build();

            // Set up event handlers
            this.setupEventHandlers();

            // Start the connection
            console.log('SignalR: Starting connection...');
            await this.connection.start();
            this.isConnected = true;
            console.log('SignalR: Connected successfully to:', url);

        } catch (error) {
            console.error('SignalR: Connection failed to:', url);
            console.error('SignalR: Error details:', error);
            this.isConnected = false;

            // Clean up connection on failure
            if (this.connection) {
                try {
                    await this.connection.stop();
                } catch (stopError) {
                    console.error('SignalR: Error stopping failed connection:', stopError);
                }
                this.connection = null;
            }

            throw error;
        }
    }

    public async disconnect(): Promise<void> {
        if (this.connection) {
            try {
                await this.connection.stop();
                this.isConnected = false;
                console.log('SignalR: Disconnected successfully');
            } catch (error) {
                console.error('SignalR: Disconnect failed', error);
            } finally {
                this.connection = null;
            }
        }
    }

    public async testConnection(url: string, token: string): Promise<boolean> {
        console.log('SignalR: Testing connection to:', url);

        try {
            // Try to fetch the negotiate endpoint
            const negotiateUrl = url + '/negotiate';
            console.log('SignalR: Testing negotiate endpoint:', negotiateUrl);

            const response = await fetch(negotiateUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('SignalR: Negotiate response status:', response.status);

            if (response.ok) {
                const negotiateData = await response.json();
                console.log('SignalR: Negotiate response:', negotiateData);
                return true;
            } else {
                console.error('SignalR: Negotiate failed with status:', response.status);
                const errorText = await response.text();
                console.error('SignalR: Negotiate error response:', errorText);
                return false;
            }
        } catch (error) {
            console.error('SignalR: Test connection failed:', error);
            return false;
        }
    }

    private setupEventHandlers(): void {
        if (!this.connection) return;

        // Handle connection events
        this.connection.onreconnecting(() => {
            console.log('SignalR: Reconnecting...');
            this.isConnected = false;
        });

        this.connection.onreconnected(() => {
            console.log('SignalR: Reconnected');
            this.isConnected = true;
        });

        this.connection.onclose((error) => {
            console.log('SignalR: Connection closed');
            if (error) {
                console.error('SignalR: Connection closed with error:', error);
            }
            this.isConnected = false;
        });

        // Add general error handler
        this.connection.on('error', (error) => {
            console.error('SignalR: General connection error:', error);
        });

        // Handle different message types
        this.connection.on('SendUpdate', (payload: SignalRPayload) => {
            try {
                console.log('SignalR: Raw payload received:', payload);
                console.log('SignalR: Payload type:', typeof payload);
                console.log('SignalR: Payload stringified:', JSON.stringify(payload, null, 2));
                
                // Check if payload is valid
                if (!payload || typeof payload !== 'object') {
                    console.error('SignalR: Invalid payload received:', payload);
                    return;
                }
                
                console.log('SignalR: Received update: SendUpdate', payload);
                switch (payload.type) {
                    case 0:
                        console.log('SignalR: Update type: Conversation');
                        emitterObject.emit(EVENT_CONSTANTS.CONVERSATION_UPDATE, payload);
                        break;
                    case 6: // MailboxIncoming, a new mail arrived
                    case 7: // MailboxUpdate, a mail was read
                        console.log('SignalR: Update type: Mailbox');
                        emitterObject.emit(EVENT_CONSTANTS.MAILBOX_UPDATE, payload);
                        break;
                    default:
                        console.log('SignalR: Unknown update type:', payload.type);
                        break;
                }
            } catch (error) {
                console.error('SignalR: Error processing SendUpdate:', error);
                console.error('SignalR: Problematic payload:', payload);
            }
        });

        // Also listen for lowercase version in case of naming differences
        this.connection.on('sendupdate', (payload: any) => {
            console.log('SignalR: Received lowercase sendupdate:', payload);
            console.log('SignalR: Payload type:', typeof payload);
            try {
                console.log('SignalR: Payload stringified:', JSON.stringify(payload, null, 2));
            } catch (error) {
                console.error('SignalR: Cannot stringify payload:', error);
            }
        });
    }
}

// Create and export a singleton instance
export const signalRService: SignalRService = new SignalRServiceImpl();
